// ═══════════════════════════════════════════════════════════
// AppShell — Two-bar navigation layout (GAB-styled)
// Bar 1: Logo + Incident Hub + Module dropdown + Incident selector
// Bar 2: Settings + user menu
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconAlertTriangle, IconSettings,
  IconLogout, IconChevronDown, IconMenu2,
} from '@tabler/icons-react';
import { GlobalSearch } from '../ui/GlobalSearch';
import { SessionTimeout } from '../ui/SessionTimeout';
import { OfflineIndicator } from '../ui/OfflineIndicator';
import PolicyChat from '../PolicyChat';
import { useAuthStore } from '../../auth/auth-store';
import { useRbacStore } from '../../rbac/rbac-store';
import { MODULES } from '../../modules';
import './AppShell.css';

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const activeIncident = useAuthStore(s => s.activeIncident);
  const incidents = useAuthStore(s => s.incidents);
  const setActiveIncident = useAuthStore(s => s.setActiveIncident);
  const getModuleAccess = useRbacStore(s => s.getModuleAccess);

  const [modulesOpen, setModulesOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const modulesRef = useRef<HTMLDivElement>(null);
  const incidentRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (modulesRef.current && !modulesRef.current.contains(e.target as Node)) setModulesOpen(false);
      if (incidentRef.current && !incidentRef.current.contains(e.target as Node)) setIncidentOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close dropdowns on navigation
  useEffect(() => {
    setModulesOpen(false);
    setIncidentOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  const visibleModules = MODULES.filter(m => getModuleAccess(m.id).canView);

  // Find current module from path
  const currentModule = MODULES.find(m => location.pathname.startsWith(m.route));

  return (
    <div className="app-shell">
      {/* Bar 1 — Primary nav */}
      <header className="app-bar-1">
        <div className="app-bar-1-left">
          <img
            src="/opengov_logo.png"
            alt="OpenGov"
            className="app-logo"
            onClick={() => navigate('/')}
          />
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/incidents')}>
            <IconAlertTriangle size={16} />
            <span className="app-bar-hide-mobile">Incident Hub</span>
          </button>

          {/* Modules dropdown */}
          <div className="dropdown" ref={modulesRef}>
            <button className="btn btn-ghost btn-sm" onClick={() => setModulesOpen(!modulesOpen)}>
              <IconMenu2 size={16} />
              <span className="app-bar-hide-mobile">{currentModule?.name || 'Modules'}</span>
              <IconChevronDown size={12} />
            </button>
            {modulesOpen && (
              <div className="dropdown-menu active" style={{ minWidth: 240 }}>
                <button className="dropdown-item" onClick={() => navigate('/')}>
                  <IconLayoutDashboard size={16} style={{ marginRight: 8 }} /> Dashboard
                </button>
                <div className="dropdown-divider" />
                {visibleModules.map(m => (
                  <button key={m.id} className="dropdown-item" onClick={() => navigate(m.route)}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, display: 'inline-block', marginRight: 8 }} />
                    {m.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="app-bar-1-right">
          {/* Incident selector */}
          <div className="dropdown" ref={incidentRef}>
            <button className="btn btn-outline btn-sm" onClick={() => setIncidentOpen(!incidentOpen)}>
              {activeIncident ? (
                <><span className="incident-dot active" /> <span className="app-bar-hide-mobile">{activeIncident.name}</span></>
              ) : (
                <span className="text-muted">No Incident</span>
              )}
              <IconChevronDown size={12} />
            </button>
            {incidentOpen && (
              <div className="dropdown-menu active right">
                {incidents.length === 0 && (
                  <div className="dropdown-item" style={{ color: 'var(--color-neutral-500)', cursor: 'default' }}>No incidents</div>
                )}
                {incidents.map(inc => (
                  <button
                    key={inc.id}
                    className="dropdown-item"
                    onClick={() => { setActiveIncident(inc); setIncidentOpen(false); }}
                    style={activeIncident?.id === inc.id ? { background: 'var(--color-primary-50)', fontWeight: 600 } : undefined}
                  >
                    <span className={`incident-dot ${inc.status === 'active' ? 'active' : ''}`} />
                    {inc.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Bar 2 — Secondary nav */}
      <div className="app-bar-2">
        <div className="app-bar-2-left" />
        <div className="app-bar-2-right">
          <GlobalSearch />
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/settings')}>
            <IconSettings size={16} />
            <span className="app-bar-hide-mobile">Settings</span>
          </button>

          {/* User menu */}
          <div className="dropdown" ref={userRef}>
            <button className="btn btn-ghost btn-sm" onClick={() => setUserOpen(!userOpen)}>
              <div className="user-avatar">{user?.firstName?.[0] || user?.email?.[0] || '?'}</div>
              <span className="app-bar-hide-mobile">{user?.firstName || user?.email || 'User'}</span>
              <IconChevronDown size={12} />
            </button>
            {userOpen && (
              <div className="dropdown-menu active right">
                <div style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-neutral-200)' }}>
                  <div className="fw-semibold text-sm">{user?.fullName || user?.email}</div>
                  <div className="text-xs text-muted">{user?.email}</div>
                </div>
                <button className="dropdown-item" onClick={() => { logout(); navigate('/login'); }} style={{ color: 'var(--color-error)' }}>
                  <IconLogout size={16} style={{ marginRight: 8 }} /> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <SessionTimeout />
      <OfflineIndicator />
      <main className="app-main">
        <Outlet />
      </main>

      {/* Floating policy assistant — available on every page */}
      <PolicyChat />
    </div>
  );
}

export default AppShell;
